import { useState } from "react";
import { Phone, Mail, ArrowRight, CalendarCheck } from "lucide-react";
import { Reveal } from "../Reveal";
import { CONTACT, TERMS, IMAGES } from "../../lib/data";
import { scrollToId } from "../../lib/scroll";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const FunnelBooking = () => {
    const [termsOpen, setTermsOpen] = useState(false);

    return (
        <>
            {/* SECTION 13 — BOOKING */}
            <section id="booking" className="relative bg-noir-surface py-24 sm:py-28 overflow-hidden" data-testid="funnel-booking">
                <div className="absolute -top-20 right-1/4 h-[360px] w-[360px] rounded-full bg-hotpink/10 blur-[120px]" aria-hidden="true" />
                <div className="relative mx-auto max-w-3xl px-4 sm:px-6 text-center">
                    <Reveal>
                        <p className="eyebrow">BOOKING</p>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                            WANT TO TALK ABOUT <span className="text-hotpink led-text-glow">YOUR CAMPAIGN?</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="mx-auto mt-5 max-w-xl text-sm sm:text-base text-zinc-400">
                            Speak directly with the WealthHaus Media team about your quote, your artwork, your
                            schedule, or your campaign options.
                        </p>
                    </Reveal>
                    <Reveal delay={0.22}>
                        <div className="mt-9 flex flex-wrap justify-center gap-4">
                            <a href={CONTACT.phoneHref} className="btn-glossy-pink !px-8 !py-4 !text-sm" data-testid="booking-call-button">
                                <CalendarCheck size={16} /> BOOK A CONSULTATION
                            </a>
                            <a href={CONTACT.mailHref} className="btn-chrome" data-testid="booking-email-button">
                                <Mail size={15} /> EMAIL US
                            </a>
                        </div>
                        <p className="mt-5 font-alt text-sm font-bold tracking-[0.14em] text-white" data-testid="booking-phone">{CONTACT.phone}</p>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 14 — FINAL CLOSE */}
            <section className="relative overflow-hidden" data-testid="funnel-close">
                <div className="absolute inset-0" aria-hidden="true">
                    <img src={IMAGES.cityNight} alt="" className="h-full w-full object-cover opacity-35" />
                    <div className="absolute inset-0 bg-gradient-to-b from-noir via-noir/70 to-noir" />
                    <div className="absolute inset-0 led-dots opacity-40" />
                </div>
                <div className="relative mx-auto max-w-4xl px-4 sm:px-6 py-28 sm:py-36 text-center">
                    <Reveal>
                        <div className="mx-auto mb-10 max-w-2xl rounded-xl border border-hotpink/40 bg-black/80 p-2 led-glow">
                            <div className="billboard-screen !aspect-[16/5]">
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-b from-[#0a0a0a] to-black">
                                    <p className="font-alt text-[10px] font-bold tracking-[0.4em] text-hotpink">WEALTHHAUS MEDIA</p>
                                    <p className="font-display text-2xl sm:text-4xl font-extrabold text-white px-4">YOUR BRAND. EVERYWHERE.</p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-display text-6xl sm:text-7xl lg:text-8xl font-extrabold leading-[0.95] tracking-wide text-white" data-testid="close-headline">
                            YOUR BUSINESS.<br />
                            <span className="text-chrome-gradient">OUR BILLBOARD.</span><br />
                            <span className="text-hotpink led-text-glow">THEIR ATTENTION.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-10 flex flex-wrap justify-center gap-4">
                            <button className="btn-glossy-pink !px-9 !py-4 !text-sm" onClick={() => scrollToId("#quote")} data-testid="close-quote-button">
                                GET MY BILLBOARD QUOTE <ArrowRight size={16} />
                            </button>
                            <a href={CONTACT.phoneHref} className="btn-chrome" data-testid="close-book-button">
                                BOOK A CONSULTATION
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 15 — CONTACT + FOOTER */}
            <footer id="contact" className="relative bg-noir border-t border-white/10 pb-28 lg:pb-10" data-testid="funnel-footer">
                <div className="mx-auto max-w-[1200px] px-4 sm:px-6 pt-16">
                    <div className="grid md:grid-cols-3 gap-10 items-start">
                        <div>
                            <p className="font-display text-xl font-extrabold text-white">WEALTHHAUS <span className="text-hotpink">MEDIA</span></p>
                            <p className="mt-1.5 font-alt text-[9px] font-bold tracking-[0.4em] text-zinc-500">YOUR BRAND. EVERYWHERE.</p>
                            <p className="mt-3 text-xs text-zinc-500">Put Your Brand in the Spotlight™</p>
                        </div>
                        <div>
                            <p className="font-alt text-[10px] font-bold tracking-[0.28em] text-zinc-400 mb-4">CONTACT</p>
                            <div className="space-y-2.5 text-sm text-zinc-300">
                                <a href={CONTACT.phoneHref} className="flex items-center gap-2.5 hover:text-white transition-colors" data-testid="footer-phone">
                                    <Phone size={15} className="text-hotpink" /> {CONTACT.phone}
                                </a>
                                <a href={CONTACT.mailHref} className="flex items-center gap-2.5 hover:text-white transition-colors" data-testid="footer-email">
                                    <Mail size={15} className="text-hotpink" /> {CONTACT.email}
                                </a>
                            </div>
                            <p className="mt-4 text-xs text-zinc-500">Ready to get your brand seen? Start your quote today.</p>
                        </div>
                        <div>
                            <p className="font-alt text-[10px] font-bold tracking-[0.28em] text-zinc-400 mb-4">LEGAL</p>
                            <button onClick={() => setTermsOpen(true)} className="block text-[13px] text-zinc-400 hover:text-white transition-colors" data-testid="footer-terms-button">
                                Terms & Conditions
                            </button>
                            <p className="mt-4 text-[11px] leading-relaxed text-zinc-600">
                                Sample billboard images are examples of advertising formats only and do not guarantee a
                                specific billboard, screen, location, market, placement, impression count, or advertising
                                inventory. All placements are subject to availability and confirmation.
                            </p>
                        </div>
                    </div>
                    <div className="mt-12 border-t border-white/8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <p className="font-alt text-[11px] tracking-[0.16em] text-zinc-600" data-testid="footer-copyright">
                            © 2026 WEALTHHAUS MEDIA. ALL RIGHTS RESERVED.
                        </p>
                        <div className="flex items-center gap-5">
                            <a href="/admin" className="font-alt text-[10px] tracking-[0.26em] text-zinc-600 hover:text-white transition-colors" data-testid="footer-admin-link">ADMIN LOGIN</a>
                            <p className="font-alt text-[10px] tracking-[0.26em] text-hotpink/70">PUT YOUR BRAND IN THE SPOTLIGHT™</p>
                        </div>
                    </div>
                </div>

                <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
                    <DialogContent className="bg-noir-elevated border-white/15 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="terms-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl text-white">Terms & Conditions</DialogTitle>
                            <DialogDescription asChild>
                                <div className="pt-4 space-y-4 text-left">
                                    {TERMS.map((t) => (
                                        <div key={t.title}>
                                            <p className="font-alt text-[12px] font-bold tracking-[0.12em] text-hotpink">{t.title}</p>
                                            <p className="mt-1 text-[13px] leading-relaxed text-zinc-400">{t.body}</p>
                                        </div>
                                    ))}
                                </div>
                            </DialogDescription>
                        </DialogHeader>
                    </DialogContent>
                </Dialog>
            </footer>
        </>
    );
};
